import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileCheck2, Search, AlertCircle, AlertTriangle, CheckCircle2, ExternalLink, Filter, ArrowLeft,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Input, Select, Spinner, EmptyState } from "@/components/ui";
import { expiryBand, daysToExpiry, fmtDate } from "./licenceHelpers";

// =============================================================================
// HrInductionsScreen — /hr/inductions
// Cross-team director/manager board. Flat table of every active induction
// across every employee. Filters for overdue / pending verify / by issuer.
// Click through to the licence detail for verify. T-005.
// =============================================================================

type StatusFilter = "all" | "pending" | "warning" | "overdue" | "verified";

interface InductionRow {
  licence_id:    string;
  employee_id:   string;
  expiry_date:   string | null;
  issue_date:    string | null;
  verified_at:   string | null;
  external_url:  string | null;
  employee?: {
    first_name: string | null;
    last_name:  string | null;
  } | null;
  licence_type?: {
    code:                  string;
    display_name:          string;
    category:              string;
    issuing_body:          string | null;
    requires_verification: boolean;
    external_system:       string | null;
  } | null;
}

export default function HrInductionsScreen() {
  const user = useRequireAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [issuerFilter, setIssuerFilter] = useState<string>("all");

  const canSee = hasRole(user, "manager");

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["hr_inductions_all"],
    enabled: !!user && canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, employee_id, expiry_date, issue_date, verified_at, external_url,
          employee:employees!employee_licences_employee_id_fkey(first_name, last_name),
          licence_type:licence_types(code, display_name, category, issuing_body, requires_verification, external_system)
        `)
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      // Filter to inductions client-side (nested-relation filter on category
      // is awkward via PostgREST; this is small data per org).
      return ((data ?? []) as unknown as InductionRow[])
        .filter(r => r.licence_type?.category === "induction");
    },
  });

  // Derived: unique issuers for the filter dropdown
  const issuers = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) {
      if (r.licence_type?.issuing_body) s.add(r.licence_type.issuing_body);
    }
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(r => {
      // Status filter
      const band = expiryBand(r.expiry_date);
      const pendingVerify = r.licence_type?.requires_verification && !r.verified_at;
      if (statusFilter === "pending"  && !pendingVerify) return false;
      if (statusFilter === "overdue"  && band !== "overdue") return false;
      if (statusFilter === "warning"  && band !== "warning") return false;
      if (statusFilter === "verified" && !r.verified_at) return false;
      // Issuer filter
      if (issuerFilter !== "all" && r.licence_type?.issuing_body !== issuerFilter) return false;
      // Search
      if (!needle) return true;
      const name = `${r.employee?.first_name ?? ""} ${r.employee?.last_name ?? ""}`.toLowerCase();
      const type = r.licence_type?.display_name?.toLowerCase() ?? "";
      return name.includes(needle) || type.includes(needle);
    });
  }, [rows, search, statusFilter, issuerFilter]);

  // Counts for tab chips
  const counts = useMemo(() => {
    let pending = 0, overdue = 0, warning = 0;
    for (const r of rows) {
      const band = expiryBand(r.expiry_date);
      if (r.licence_type?.requires_verification && !r.verified_at) pending++;
      if (band === "overdue") overdue++;
      else if (band === "warning") warning++;
    }
    return { pending, overdue, warning, total: rows.length };
  }, [rows]);

  if (!user) return null;
  if (!canSee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-text-muted">
            Only directors and managers can view the induction board.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto pb-12">
      <Link to="/hr" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> HR hub
      </Link>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-10 bg-accent rounded-full" />
          <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">HR · inductions</div>
        </div>
        <h1 className="text-2xl font-semibold">Induction oversight</h1>
        <p className="text-[13px] text-text-muted mt-0.5">
          Every active induction across the team. Filter by status or issuing body.
        </p>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <ChipCard label="Total"    value={counts.total}    onClick={() => setStatusFilter("all")} active={statusFilter === "all"} />
        <ChipCard label="Pending verify" value={counts.pending} onClick={() => setStatusFilter("pending")} active={statusFilter === "pending"} tone={counts.pending > 0 ? "warn" : "neutral"} />
        <ChipCard label="Overdue"  value={counts.overdue}  onClick={() => setStatusFilter("overdue")} active={statusFilter === "overdue"} tone={counts.overdue > 0 ? "danger" : "neutral"} />
        <ChipCard label="≤60 days" value={counts.warning}  onClick={() => setStatusFilter("warning")} active={statusFilter === "warning"} tone={counts.warning > 0 ? "warn" : "neutral"} />
      </div>

      {/* Filters */}
      <Card>
        <div className="p-3 flex flex-wrap items-center gap-3 border-b border-line">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <Input
              placeholder="Employee or induction name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex items-center gap-2 text-[12px] text-text-muted">
            <Filter size={13} />
            <Select value={issuerFilter} onChange={e => setIssuerFilter(e.target.value)} className="max-w-[200px]">
              <option value="all">All issuers</option>
              {issuers.map(i => <option key={i} value={i}>{i}</option>)}
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="py-10 flex justify-center"><Spinner /></div>
        ) : error ? (
          <div className="p-6 text-[13px] text-red-400">
            Can't load inductions: {error instanceof Error ? error.message : String(error)}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileCheck2}
            title={rows.length === 0 ? "No inductions recorded" : "No results"}
            description={rows.length === 0
              ? "Once employees record inductions they'll appear here."
              : "Adjust the filters above."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted border-b border-line">
                  <th className="px-4 py-2 font-medium">Employee</th>
                  <th className="px-4 py-2 font-medium">Induction</th>
                  <th className="px-4 py-2 font-medium">Issuer</th>
                  <th className="px-4 py-2 font-medium">Expiry</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Link</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const name = `${r.employee?.first_name ?? ""} ${r.employee?.last_name ?? ""}`.trim() || "(unknown)";
                  const band = expiryBand(r.expiry_date);
                  const days = daysToExpiry(r.expiry_date);
                  const pendingVerify = r.licence_type?.requires_verification && !r.verified_at;
                  return (
                    <tr key={r.licence_id} className="border-b border-line/50 hover:bg-ink-raised/50">
                      <td className="px-4 py-2.5 text-text">{name}</td>
                      <td className="px-4 py-2.5">
                        <Link to={`/hr/licences/${r.employee_id}`} className="hover:underline">
                          <span className="font-mono text-[11px] text-accent">{r.licence_type?.code ?? "—"}</span>
                          <span className="block text-text">{r.licence_type?.display_name ?? "—"}</span>
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-text-muted">{r.licence_type?.issuing_body ?? "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px]">
                        {r.expiry_date ? (
                          <span className={band === "overdue" ? "text-danger" : band === "warning" ? "text-warn" : ""}>
                            {fmtDate(r.expiry_date)}
                            {days != null && <span className="ml-1 text-text-muted">· {days < 0 ? `${Math.abs(days)}d` : `${days}d`}</span>}
                          </span>
                        ) : (
                          <span className="text-text-dim">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {pendingVerify ? <Pill label="pending" tone="pending" small />
                          : r.verified_at ? <Pill label="verified" tone="ok" small />
                          : band === "overdue" ? <Pill label="overdue" tone="critical" small />
                          : band === "warning" ? <Pill label="≤60d" tone="warning" small />
                          : <span className="text-text-dim text-[12px]">—</span>}
                        {pendingVerify && <AlertCircle size={12} className="inline ml-1 text-warn" />}
                        {!pendingVerify && r.verified_at && <CheckCircle2 size={12} className="inline ml-1 text-ok" />}
                        {band === "overdue" && <AlertTriangle size={12} className="inline ml-1 text-danger" />}
                      </td>
                      <td className="px-4 py-2.5">
                        {r.external_url ? (
                          <a href={r.external_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline inline-flex items-center gap-1">
                            <ExternalLink size={11} /> open
                          </a>
                        ) : (
                          <span className="text-text-dim">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------

function ChipCard({
  label, value, onClick, active, tone = "neutral",
}: {
  label: string; value: number; onClick: () => void; active: boolean;
  tone?: "neutral" | "warn" | "danger";
}) {
  const valueColour =
    tone === "danger" ? "text-danger" :
    tone === "warn"   ? "text-warn"   :
    "text-text";
  return (
    <button
      onClick={onClick}
      type="button"
      className={`text-left rounded-md border p-3 transition-colors ${
        active ? "border-accent bg-accent/10" : "border-line bg-ink-raised hover:border-line-light"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
      <div className={`mt-1 text-[20px] font-semibold ${valueColour}`}>{value}</div>
    </button>
  );
}
